// ---------------------------------------------------------------------------
// The browser side of RISK RADAR. V29.
//
// NO VOCABULARY IS SPELLED IN THIS FILE OR IN THE SCREEN THAT USES IT. A feature
// arrives as an id AND its label AND the sentence explaining it; a confidence
// level arrives with its label and its TONE, so the colour of a state is a
// property of the state (`0072`); an action arrives with its label, its effort
// and whether the client has to agree; the acts a human may take arrive as rows
// from `/v1/reference/risk-vocabulary` with `needs_action` saying which of them
// name one. `verify-V29.sh` §5 reads all five vocabularies out of the database
// and greps this directory for the quoted form of every value.
//
// THE SCREEN COMPUTES NO SCORE, NO SHORTFALL, NO EXPECTED GAIN AND NO SENTENCE.
// Every number here was produced by `app.assess_event_risk` and stored, which is
// what makes what a scheduler is shown today the same as what a tribunal reads in
// a year. A component that recomputed a gain would be a second model, and the one
// nobody checked would be the one on the screen.
// ---------------------------------------------------------------------------

import { mutate, read } from "./console";

export type RiskRun = {
  run_id: string;
  assessed_at: string;
  assessed_by: string | null;
  /** How far ahead the question was asked. "Risk 74" means nothing without it. */
  horizon_hours: number;
  model_version: string;
  /** Completed shifts behind this organisation. Zero forces low confidence. */
  history_shifts: number;
  requirement_count: number;
  highest_risk: string | null;
  scored_count: number;
  /** Null until at least one post in this run has happened and been scored. */
  mean_absolute_error: string | null;
  override_count: number;
  retain_until: string;
};

export type RiskPrediction = {
  prediction_id: string;
  requirement_id: string;
  role_name: string;
  zone: string | null;
  starts_at: string;
  ends_at: string;
  risk: string;
  predicted_shortfall: string;
  headcount: number;
  open_seats: number;
  confidence: string;
  confidence_label: string;
  confidence_tone: string;
  factors_measured: number;
  /**
   * THE ONE SENTENCE THAT GOES ON THE ROW: the measured term carrying the
   * largest share of this score, chosen by the model. Never assembled here — a
   * browser cutting `explanation` at a full stop would be a second explanation,
   * written where nobody checks it.
   */
  headline: string;
  /** All ten terms. Never empty; the database refuses a row without one. */
  explanation: string;
  action_count: number;
  actual_shortfall: string | null;
  absolute_error: string | null;
  decided: boolean;
  decided_action: string | null;
};

export type RiskFactorLine = {
  factor_id: string;
  factor_label: string;
  /**
   * FALSE MEANS THE TERM WAS LEFT OUT OF THE SCORE ENTIRELY — not that it scored
   * zero. Two of the ten can never be measured in this build, and a screen that
   * drew them as calm would be reporting confidence it does not have.
   */
  is_measurable: boolean;
  points: string | null;
  weight: string;
  /** This term's share of the risk score. These sum exactly to the score. */
  contribution: string;
  sentence: string;
};

export type RiskActionLine = {
  action_id: string;
  action_type_id: string;
  action_label: string;
  rank: number;
  expected_gain: string;
  cost_delta_minor: string;
  effort_label: string;
  needs_client_approval: boolean;
  sentence: string;
  decided: boolean;
  decided_action: string | null;
  decided_by: string | null;
  decided_at: string | null;
};

export type RiskOverride = {
  override_id: string;
  prediction_id: string;
  risk_action_id: string | null;
  action_id: string;
  action_label: string;
  reason: string | null;
  decided_by: string | null;
  decided_at: string;
};

export type RiskVocabulary = {
  risk_factors: { id: string; label: string; weight: number; rank: number; description: string }[];
  risk_action_types: {
    id: string;
    label: string;
    rank: number;
    remedies_factor_id: string;
    cost_per_seat_minor: string;
    effort_label: string;
    needs_client_approval: boolean;
    is_available: boolean;
    description: string;
  }[];
  risk_confidence_levels: {
    id: string;
    label: string;
    rank: number;
    tone: string;
    min_factors_measured: number;
    allowed_without_history: boolean;
    description: string;
  }[];
  risk_override_actions: {
    id: string;
    label: string;
    rank: number;
    needs_action: boolean;
    description: string;
  }[];
  /** What the model may never read. Served because an absence nobody can see is
   *  indistinguishable from a promise nobody kept. */
  risk_prohibited_signals: { id: string; label: string; description: string }[];
};

export const risk = {
  vocabulary: (base: string) => read(base, "/v1/reference/risk-vocabulary"),

  runs: (base: string, eventId: string) =>
    read(base, `/v1/events/${encodeURIComponent(eventId)}/risk-runs`),

  assess: (base: string, eventId: string, horizonHours?: number) =>
    mutate(
      "POST",
      base,
      `/v1/events/${encodeURIComponent(eventId)}/risk-runs`,
      horizonHours === undefined ? {} : { horizon_hours: String(horizonHours) },
    ),

  run: (base: string, runId: string) => read(base, `/v1/risk-runs/${encodeURIComponent(runId)}`),

  score: (base: string, runId: string) =>
    mutate("POST", base, `/v1/risk-runs/${encodeURIComponent(runId)}/score`, {}),

  prediction: (base: string, predictionId: string) =>
    read(base, `/v1/risk-predictions/${encodeURIComponent(predictionId)}`),

  decide: (
    base: string,
    predictionId: string,
    payload: { action: string; risk_action_id?: string | null; reason?: string | null },
  ) =>
    mutate("POST", base, `/v1/risk-predictions/${encodeURIComponent(predictionId)}/decisions`, payload),
};
